// [matrah] Toast helpers on top of the global store's setAlertMessage / toast queue
// (rendered by components/global/Notification.vue, mounted once in App.vue).
//
//   toastSuccess('Saved')                 green, auto-hide
//   toastError('Could not save')          red, auto-hide (longer)
//   toastWarning / toastInfo
//   toastApiError(error, 'Could not save the welcome message')
//        → "Could not save the welcome message: <server message>" and marks the
//          axios error as handled so the central interceptor (api/api.js) does
//          not show a second, generic toast for the same failure.
import { useGlobalStore } from '../stores/global'

const DEFAULT_TIMEOUT = { success: 3000, info: 3500, warning: 4500, error: 6000 }

export function toast(type, text, options = {}) {
  if (!text) return
  const kind = ['success', 'error', 'warning', 'info'].includes(type) ? type : 'info'
  useGlobalStore().setAlertMessage({
    display: true,
    text: String(text),
    class: kind,
    timeout: options.timeout ?? DEFAULT_TIMEOUT[kind],
  })
}

export const toastSuccess = (text, options) => toast('success', text, options)
export const toastError = (text, options) => toast('error', text, options)
export const toastWarning = (text, options) => toast('warning', text, options)
export const toastInfo = (text, options) => toast('info', text, options)

/** Prevents the central interceptor from toasting this error (the caller shows its own). */
export function markHandled(error) {
  if (error && typeof error === 'object') {
    try {
      error.__toastHandled = true
    } catch (e) {
      /* frozen object — ignore */
    }
  }
  return error
}

function pickMessage(value) {
  if (!value) return ''
  if (typeof value === 'string') {
    const s = value.trim()
    // ignore HTML error pages (Express default 404 / proxy errors)
    if (!s || s.startsWith('<')) return ''
    return s.length > 240 ? `${s.slice(0, 237)}…` : s
  }
  if (typeof value === 'object') {
    return pickMessage(value.error_user_msg || value.message || value.error?.message || (typeof value.error === 'string' ? value.error : ''))
  }
  return ''
}

/** Human-readable reason for a failed request (network, server message, validation errors). */
export function apiErrorReason(error) {
  if (!error) return ''
  if (!error.response) {
    if (error.code === 'ECONNABORTED') return 'the server took too long to respond'
    if (error.message && !/network error/i.test(error.message) && !error.config) return pickMessage(error.message)
    return 'cannot reach the server — check your connection'
  }
  const { status, data } = error.response
  let reason = pickMessage(data?.message) || pickMessage(data?.error) || pickMessage(typeof data?.data === 'string' ? data.data : '')
  if (!reason && Array.isArray(data?.errors)) {
    reason = data.errors
      .map((e) => (typeof e === 'string' ? e : e?.message || e?.msg))
      .filter(Boolean)
      .slice(0, 3)
      .join(' · ')
  }
  if (!reason) reason = pickMessage(typeof data === 'string' ? data : '')
  if (!reason) {
    if (status >= 500) reason = `server error (HTTP ${status})`
    else if (status === 404) reason = 'not found (HTTP 404)'
    else if (status === 403) reason = 'you do not have permission (HTTP 403)'
    else reason = `request failed (HTTP ${status})`
  }
  return reason
}

/** "<fallback>: <reason>" */
export function apiErrorMessage(error, fallback = 'Something went wrong') {
  const reason = apiErrorReason(error)
  if (!fallback) return reason || 'Something went wrong'
  return reason ? `${fallback}: ${reason}` : fallback
}

/** Error toast for a failed API call + mark it handled (no duplicate interceptor toast). */
export function toastApiError(error, fallback, options) {
  markHandled(error)
  toastError(apiErrorMessage(error, fallback), options)
}
